import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../../config';
import { useHeadToHead } from '../../hooks/useHeadToHead';
import { H2HHeader } from './H2HHeader';
import { H2HDimensionRow } from './H2HDimensionRow';
import { H2HRadarChart } from './H2HRadarChart';
import { H2HDirectMatchesList } from './H2HDirectMatchesList';
import { PeriodSelector, Period } from './PeriodSelector';

interface SeasonsResponse { seasons: Array<{ id: string; name: string }>; }

export function HeadToHeadPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const [params] = useSearchParams();
  const userA = params.get('a');
  const userB = params.get('b');
  const initialSeasonId = params.get('seasonId');

  const [current, setCurrent] = useState<Period>({ id: initialSeasonId, label: initialSeasonId ? '...' : 'Todos los tiempos' });
  const [seasons, setSeasons] = useState<Period[]>([]);

  useEffect(() => {
    if (!groupId) return;
    const token = localStorage.getItem('authToken');
    fetch(getApiUrl(`/groups/${groupId}/seasons`), {
      headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
    })
      .then((r) => r.json() as Promise<SeasonsResponse>)
      .then((j) => {
        const ss = (j.seasons ?? []).map((s) => ({ id: s.id, label: s.name }));
        setSeasons(ss);
        if (initialSeasonId) {
          const match = ss.find((s) => s.id === initialSeasonId);
          if (match) setCurrent(match);
        }
      });
  }, [groupId, initialSeasonId]);

  const periods = useMemo<Period[]>(() => [{ id: null, label: 'Todos los tiempos' }, ...seasons], [seasons]);

  const { data, loading, error } = useHeadToHead({
    groupId: groupId!,
    userA: userA!,
    userB: userB!,
    seasonId: current.id,
  });

  if (!groupId || !userA || !userB) return <div style={empty}>Faltan parámetros.</div>;
  if (loading) return <div style={empty}>Cargando…</div>;
  if (error || !data) return <div style={empty}>No se pudo cargar la comparativa.</div>;

  return (
    <div style={{ maxWidth: 1024, margin: '0 auto', padding: 16 }}>
      <h2 style={{ color: '#fff' }}>⚔️ Enfrentamiento — {data.scope.groupName}</h2>
      <PeriodSelector periods={periods} current={current} onChange={setCurrent} />
      {data.warning && <div style={{ color: '#ffb300', padding: 12 }}>{data.warning}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div>
          <H2HRadarChart dimensions={data.dimensions} nameA={data.players.a.name} nameB={data.players.b.name} />
        </div>
        <div>
          <H2HHeader a={data.players.a} b={data.players.b} globalWinner={data.globalWinner} />
          {data.dimensions.map((d) => (
            <H2HDimensionRow key={d.key} dim={d} />
          ))}
        </div>
      </div>

      <H2HDirectMatchesList matches={data.directMatches} a={data.players.a} b={data.players.b} />
    </div>
  );
}

const empty: React.CSSProperties = { color: '#888', textAlign: 'center', padding: 40 };
